import { Archive, BookOpen, LoaderCircle, Search, Utensils } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import type { LibraryFoodSummary, RecipeSummary } from '@calorie-tracker/api';
import type { MealType } from '@calorie-tracker/nutrition';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { trpc } from '@/lib/trpc';

import { fieldClassName } from './constants';
import {
  formatLocalTimestamp,
  formatMealTypeLabel,
  formatNumber,
  getErrorMessage,
} from './helpers';

type LibraryTab = 'foods' | 'recipes' | 'meals';
type Serialized<T> = T extends Date
  ? string
  : T extends Array<infer Item>
    ? Serialized<Item>[]
    : T extends object
      ? { [Key in keyof T]: Serialized<T[Key]> }
      : T;
type AppLibraryFoodSummary = Serialized<LibraryFoodSummary>;
type AppRecipeSummary = Serialized<RecipeSummary>;

interface LogSelectionState {
  amount: string;
  mealType: MealType;
  unit: string;
}

function createFoodLogState(summary: AppLibraryFoodSummary): LogSelectionState {
  return {
    amount: `${summary.food.defaultQuantity.amount}`,
    unit: summary.food.defaultQuantity.unit,
    mealType: 'snack',
  };
}

function createRecipeLogState(summary: AppRecipeSummary): LogSelectionState {
  return {
    amount: `${summary.latestVersion.totalYield.amount}`,
    unit: summary.latestVersion.totalYield.unit,
    mealType: 'snack',
  };
}

function MacroLine({
  macros,
}: {
  macros: {
    calories: number;
    carbsGrams: number;
    fatGrams: number;
    proteinGrams: number;
  };
}) {
  return (
    <span>
      {formatNumber(macros.calories)} cal · {formatNumber(macros.proteinGrams)}P{' '}
      {formatNumber(macros.carbsGrams)}C {formatNumber(macros.fatGrams)}F
    </span>
  );
}

function MealTypeSelect({
  onChange,
  value,
}: {
  onChange: (value: MealType) => void;
  value: MealType;
}) {
  return (
    <select
      className={fieldClassName}
      onChange={(event) => onChange(event.target.value as MealType)}
      value={value}
    >
      {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((mealType) => (
        <option key={mealType} value={mealType}>
          {formatMealTypeLabel(mealType)}
        </option>
      ))}
    </select>
  );
}

export function LibraryScreen({ todayDate }: { todayDate: string }) {
  const utils = trpc.useUtils();
  const [activeTab, setActiveTab] = useState<LibraryTab>('foods');
  const [query, setQuery] = useState('');
  const [selectedFood, setSelectedFood] = useState<AppLibraryFoodSummary | null>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<AppRecipeSummary | null>(null);
  const [foodLogState, setFoodLogState] = useState<LogSelectionState | null>(null);
  const [recipeLogState, setRecipeLogState] = useState<LogSelectionState | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const foodsQuery = trpc.library.foods.useQuery(
    {
      limit: 50,
      ...(query.trim().length > 0 ? { query: query.trim() } : {}),
    },
    {
      retry: false,
    },
  );
  const recipesQuery = trpc.log.listRecipes.useQuery(undefined, {
    retry: false,
  });
  const frequentMealsQuery = trpc.library.frequentMeals.useQuery(
    {
      limit: 20,
    },
    {
      retry: false,
    },
  );
  const logFood = trpc.library.logFood.useMutation();
  const archiveFood = trpc.library.archiveFood.useMutation();
  const logRecipe = trpc.log.logRecipeServing.useMutation();
  const foods = foodsQuery.data ?? [];
  const recipes = recipesQuery.data ?? [];
  const frequentMeals = frequentMealsQuery.data ?? [];
  const isMutating = logFood.isPending || archiveFood.isPending || logRecipe.isPending;

  function selectFood(summary: AppLibraryFoodSummary) {
    setSelectedFood(summary);
    setSelectedRecipe(null);
    setFoodLogState(createFoodLogState(summary));
    setRecipeLogState(null);
    setActionError(null);
    setActionSuccess(null);
  }

  function selectRecipe(summary: AppRecipeSummary) {
    setSelectedRecipe(summary);
    setSelectedFood(null);
    setRecipeLogState(createRecipeLogState(summary));
    setFoodLogState(null);
    setActionError(null);
    setActionSuccess(null);
  }

  function updateFoodLogField<Key extends keyof LogSelectionState>(
    key: Key,
    value: LogSelectionState[Key],
  ) {
    setFoodLogState((currentState) =>
      currentState
        ? {
            ...currentState,
            [key]: value,
          }
        : currentState,
    );
  }

  function updateRecipeLogField<Key extends keyof LogSelectionState>(
    key: Key,
    value: LogSelectionState[Key],
  ) {
    setRecipeLogState((currentState) =>
      currentState
        ? {
            ...currentState,
            [key]: value,
          }
        : currentState,
    );
  }

  async function invalidateLogViews(date: string) {
    await Promise.all([
      utils.today.summary.invalidate({ date }),
      utils.history.days.invalidate({
        beforeDate: todayDate,
        limit: 14,
      }),
      utils.library.foods.invalidate(),
    ]);
  }

  async function submitFoodLog(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!selectedFood || !foodLogState) {
      return;
    }

    const amount = Number(foodLogState.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      setActionError('Enter a valid quantity before logging this food.');
      return;
    }

    setActionError(null);
    setActionSuccess(null);

    try {
      const result = await logFood.mutateAsync({
        consumedAt: formatLocalTimestamp(new Date()),
        foodId: selectedFood.food.id,
        mealType: foodLogState.mealType,
        quantity: {
          amount,
          unit: foodLogState.unit,
        },
      });

      await invalidateLogViews(result.date);
      setActionSuccess(
        `${result.food.name} saved to ${formatMealTypeLabel(result.meal.mealType)}.`,
      );
      setSelectedFood(null);
      setFoodLogState(null);
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  async function submitRecipeLog(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!selectedRecipe || !recipeLogState) {
      return;
    }

    const amount = Number(recipeLogState.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      setActionError('Enter a valid quantity before logging this recipe.');
      return;
    }

    setActionError(null);
    setActionSuccess(null);

    try {
      const result = await logRecipe.mutateAsync({
        consumedAt: formatLocalTimestamp(new Date()),
        mealType: recipeLogState.mealType,
        recipeId: selectedRecipe.recipe.id,
        servingQuantity: {
          amount,
          unit: recipeLogState.unit,
        },
      });

      await invalidateLogViews(result.date);
      setActionSuccess(
        `${result.recipe.name} saved to ${formatMealTypeLabel(result.meal.mealType)}.`,
      );
      setSelectedRecipe(null);
      setRecipeLogState(null);
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  async function archiveSelectedFood() {
    if (!selectedFood) {
      return;
    }

    setActionError(null);
    setActionSuccess(null);

    try {
      await archiveFood.mutateAsync({
        foodId: selectedFood.food.id,
      });
      await utils.library.foods.invalidate();
      setActionSuccess(`${selectedFood.food.name} removed from Foods.`);
      setSelectedFood(null);
      setFoodLogState(null);
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Library</p>
          <h2 className="text-2xl font-semibold">Reusable foods</h2>
        </div>

        <div className="grid grid-cols-3 rounded-md bg-secondary p-1">
          {(['foods', 'recipes', 'meals'] as const).map((tab) => (
            <button
              className={`rounded px-3 py-2 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground'
              }`}
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setActionError(null);
                setActionSuccess(null);
              }}
              type="button"
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'foods' ? (
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <input
              className={`${fieldClassName} w-full pl-9`}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search foods"
              value={query}
            />
          </label>
        ) : null}

        {actionError ? (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {actionError}
          </p>
        ) : null}

        {actionSuccess ? (
          <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {actionSuccess}
          </p>
        ) : null}
      </Card>

      {activeTab === 'foods' ? (
        <Card className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Foods</p>
              <h3 className="mt-1 text-xl font-semibold">Canonical foods</h3>
            </div>
            {foodsQuery.isPending ? (
              <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
            ) : null}
          </div>

          {foodsQuery.error ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {getErrorMessage(foodsQuery.error)}
            </p>
          ) : null}

          {foodsQuery.isPending ? (
            <div className="flex items-center gap-3 rounded-md border border-border/70 bg-background px-3 py-4 text-sm text-muted-foreground">
              <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
              Loading foods
            </div>
          ) : foods.length === 0 ? (
            <div className="border border-dashed border-border bg-background px-3 py-5 text-sm leading-6 text-muted-foreground">
              Confirmed foods will appear here after you log them.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {foods.map((summary) => (
                <button
                  className={`flex w-full items-start justify-between gap-3 py-3 text-left ${
                    selectedFood?.food.id === summary.food.id ? 'text-foreground' : ''
                  }`}
                  key={summary.food.id}
                  onClick={() => selectFood(summary)}
                  type="button"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{summary.food.name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatNumber(summary.food.defaultQuantity.amount)}{' '}
                      {summary.food.defaultQuantity.unit} ·{' '}
                      <MacroLine macros={summary.currentVersion.macros} />
                    </p>
                  </div>
                  <span className="shrink-0 rounded border border-border px-2 py-1 text-xs">
                    {summary.food.logCount}x
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>
      ) : null}

      {activeTab === 'recipes' ? (
        <Card className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Recipes</p>
              <h3 className="mt-1 text-xl font-semibold">Saved recipes</h3>
            </div>
            {recipesQuery.isPending ? (
              <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            )}
          </div>

          {recipesQuery.error ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {getErrorMessage(recipesQuery.error)}
            </p>
          ) : null}

          {recipes.length === 0 ? (
            <div className="border border-dashed border-border bg-background px-3 py-5 text-sm leading-6 text-muted-foreground">
              Saved recipes will appear here.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recipes.map((summary) => (
                <button
                  className="flex w-full items-start justify-between gap-3 py-3 text-left"
                  key={summary.recipe.id}
                  onClick={() => selectRecipe(summary)}
                  type="button"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{summary.recipe.name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatNumber(summary.latestVersion.totalYield.amount)}{' '}
                      {summary.latestVersion.totalYield.unit} ·{' '}
                      <MacroLine macros={summary.latestVersion.totalMacros} />
                    </p>
                  </div>
                  <span className="shrink-0 rounded border border-border px-2 py-1 text-xs">
                    Log
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>
      ) : null}

      {activeTab === 'meals' ? (
        <Card className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Meals</p>
              <h3 className="mt-1 text-xl font-semibold">Frequent meals</h3>
            </div>
            {frequentMealsQuery.isPending ? (
              <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <Utensils className="h-4 w-4 text-muted-foreground" />
            )}
          </div>

          {frequentMealsQuery.error ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {getErrorMessage(frequentMealsQuery.error)}
            </p>
          ) : null}

          {frequentMeals.length === 0 ? (
            <div className="border border-dashed border-border bg-background px-3 py-5 text-sm leading-6 text-muted-foreground">
              Repeated meals will appear here.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {frequentMeals.map((summary) => (
                <div className="py-3" key={summary.key}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">
                        {summary.entries.map((entry) => entry.name).join(', ')}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        <MacroLine macros={summary.totals} />
                      </p>
                    </div>
                    <span className="shrink-0 rounded border border-border px-2 py-1 text-xs">
                      {summary.logCount}x
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : null}

      {selectedFood && foodLogState ? (
        <Card className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Log food</p>
              <h3 className="mt-1 text-xl font-semibold">{selectedFood.food.name}</h3>
            </div>
            <Button
              className="text-muted-foreground"
              disabled={isMutating}
              onClick={archiveSelectedFood}
              size="sm"
              type="button"
              variant="outline"
            >
              <Archive className="h-4 w-4" />
              Hide
            </Button>
          </div>

          <form className="space-y-3" onSubmit={submitFoodLog}>
            <div className="grid grid-cols-[1fr_0.8fr] gap-3">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Amount</span>
                <input
                  className={fieldClassName}
                  min="0.1"
                  onChange={(event) => updateFoodLogField('amount', event.target.value)}
                  required
                  step="0.1"
                  type="number"
                  value={foodLogState.amount}
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Unit</span>
                <input
                  className={fieldClassName}
                  onChange={(event) => updateFoodLogField('unit', event.target.value)}
                  required
                  value={foodLogState.unit}
                />
              </label>
            </div>

            <label className="grid gap-2 text-sm">
              <span className="font-medium">Meal</span>
              <MealTypeSelect
                onChange={(value) => updateFoodLogField('mealType', value)}
                value={foodLogState.mealType}
              />
            </label>

            <Button disabled={isMutating} type="submit">
              {logFood.isPending ? (
                <>
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  Logging
                </>
              ) : (
                'Log food'
              )}
            </Button>
          </form>
        </Card>
      ) : null}

      {selectedRecipe && recipeLogState ? (
        <Card className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground">Log recipe</p>
            <h3 className="mt-1 text-xl font-semibold">{selectedRecipe.recipe.name}</h3>
          </div>

          <form className="space-y-3" onSubmit={submitRecipeLog}>
            <div className="grid grid-cols-[1fr_0.8fr] gap-3">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Amount</span>
                <input
                  className={fieldClassName}
                  min="0.1"
                  onChange={(event) => updateRecipeLogField('amount', event.target.value)}
                  required
                  step="0.1"
                  type="number"
                  value={recipeLogState.amount}
                />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Unit</span>
                <input
                  className={fieldClassName}
                  onChange={(event) => updateRecipeLogField('unit', event.target.value)}
                  required
                  value={recipeLogState.unit}
                />
              </label>
            </div>

            <label className="grid gap-2 text-sm">
              <span className="font-medium">Meal</span>
              <MealTypeSelect
                onChange={(value) => updateRecipeLogField('mealType', value)}
                value={recipeLogState.mealType}
              />
            </label>

            <Button disabled={isMutating} type="submit">
              {logRecipe.isPending ? (
                <>
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  Logging
                </>
              ) : (
                'Log recipe'
              )}
            </Button>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
